import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type QuickCreateEntityKind = 'category' | 'brand';

export interface QuickCreatedEntity {
  id?: number;
  name: string;
  description?: string;
}

interface QuickCreateEntityModalProps {
  kind: QuickCreateEntityKind;
  onClose: () => void;
  onCreate: (entity: QuickCreatedEntity) => void | Promise<void>;
}

const QuickCreateEntityModal: React.FC<QuickCreateEntityModalProps> = ({
  kind,
  onClose,
  onCreate,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isCategory = kind === 'category';

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const submitEntity = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = name.trim();
    if (!normalizedName) {
      setError(t(isCategory ? 'admin.products.quick_create.validation.category_name' : 'admin.products.quick_create.validation.brand_name'));
      return;
    }
    if (isCategory && !description.trim()) {
      setError(t('admin.products.quick_create.validation.category_description'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await onCreate({
        name: normalizedName,
        description: isCategory ? description.trim() : undefined,
      });
    } catch (caughtError: unknown) {
      setError(caughtError instanceof Error ? caughtError.message : t('admin.products.quick_create.validation.create_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/45 p-4">
      <section
        className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-create-title"
      >
        <header className="flex items-start justify-between gap-4 border-b border-outline-variant px-5 py-4">
          <div>
            <h2 id="quick-create-title" className="font-h2 text-h2 text-on-surface">
              {t(isCategory ? 'admin.products.quick_create.category_title' : 'admin.products.quick_create.brand_title')}
            </h2>
            <p className="mt-1 font-body-sm text-body-sm text-secondary">
              {t(isCategory ? 'admin.products.quick_create.category_description' : 'admin.products.quick_create.brand_description')}
            </p>
          </div>
          <button
            type="button"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-secondary hover:bg-surface-container"
            onClick={onClose}
            aria-label={t('admin.products.quick_create.close')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <form onSubmit={submitEntity}>
          <div className="space-y-4 px-5 py-5">
            <label className="block font-label-md text-label-md text-on-surface">
              {t(isCategory ? 'admin.products.quick_create.category_name' : 'admin.products.quick_create.brand_name')} <span className="text-error">*</span>
              <input
                className="mt-1.5 h-11 w-full rounded-xl border border-outline-variant px-3 font-body-md text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setError('');
                }}
                placeholder={t(isCategory ? 'admin.products.quick_create.category_placeholder' : 'admin.products.quick_create.brand_placeholder')}
                autoFocus
              />
            </label>

            {isCategory && (
              <label className="block font-label-md text-label-md text-on-surface">
                {t('admin.products.quick_create.description')} <span className="text-error">*</span>
                <textarea
                  className="mt-1.5 min-h-24 w-full resize-y rounded-xl border border-outline-variant px-3 py-2 font-body-md text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setError('');
                  }}
                  placeholder={t('admin.products.quick_create.description_placeholder')}
                  required
                />
              </label>
            )}

            {error && <p className="font-body-sm text-body-sm text-error" role="alert">{error}</p>}
          </div>

          <footer className="flex justify-end gap-2 border-t border-outline-variant bg-surface-lowest px-5 py-4">
            <button
              type="button"
              className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-label-md text-secondary hover:bg-surface-container-low"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('admin.products.quick_create.cancel')}
            </button>
            <button
              type="submit"
              className="min-h-10 rounded-xl bg-primary px-4 font-label-md text-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('admin.products.quick_create.creating') : t('admin.products.quick_create.create_select')}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default QuickCreateEntityModal;
